import { Component, ErrorInfo, ReactNode } from 'react';
import { pickByLang, normalizeLangCode, DEFAULT_LANG } from '@/i18n';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

const i18nError: Record<string, { title: string; desc: string; retry: string; home: string }> = {
  ko: { title: '문제가 발생했습니다', desc: '예상치 못한 오류가 발생했습니다. 다시 시도하거나 홈으로 이동해 주세요.', retry: '다시 시도', home: '홈으로 이동' },
  ja: { title: 'エラーが発生しました', desc: '予期しないエラーが発生しました。再試行するか、ホームに戻ってください。', retry: '再試行', home: 'ホームへ' },
  th: { title: 'เกิดข้อผิดพลาด', desc: 'เกิดข้อผิดพลาดที่ไม่คาดคิด กรุณาลองใหม่หรือกลับหน้าแรก', retry: 'ลองใหม่', home: 'หน้าแรก' },
  vi: { title: 'Đã xảy ra lỗi', desc: 'Đã xảy ra lỗi không mong muốn. Vui lòng thử lại hoặc quay về trang chủ.', retry: 'Thử lại', home: 'Trang chủ' },
  id: { title: 'Terjadi Kesalahan', desc: 'Terjadi kesalahan yang tidak terduga. Silakan coba lagi atau kembali ke beranda.', retry: 'Coba lagi', home: 'Beranda' },
  en: { title: 'Something went wrong', desc: 'An unexpected error occurred. Please retry or go back home.', retry: 'Retry', home: 'Go Home' },
  ms: { title: 'Ralat berlaku', desc: 'Ralat tidak dijangka berlaku. Sila cuba lagi atau kembali ke laman utama.', retry: 'Cuba lagi', home: 'Laman Utama' },
  de: { title: 'Etwas ist schiefgelaufen', desc: 'Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es erneut oder gehen Sie zur Startseite.', retry: 'Erneut versuchen', home: 'Zur Startseite' },
  es: { title: 'Se produjo un error', desc: 'Ocurrió un error inesperado. Inténtalo de nuevo o vuelve al inicio.', retry: 'Reintentar', home: 'Ir al inicio' },
  fr: { title: 'Une erreur est survenue', desc: 'Une erreur inattendue est survenue. Réessayez ou revenez à l’accueil.', retry: 'Réessayer', home: 'Accueil' },
  ar: { title: 'حدث خطأ ما', desc: 'وقع خطأ غير متوقع. يرجى المحاولة مرة أخرى أو العودة إلى الصفحة الرئيسية.', retry: 'إعادة المحاولة', home: 'الصفحة الرئيسية' },
  'pt-BR': { title: 'Ocorreu um erro', desc: 'Ocorreu um erro inesperado. Tente novamente ou volte para a pagina inicial.', retry: 'Tentar novamente', home: 'Ir para Inicio' },
  my: { title: 'Something went wrong', desc: 'An unexpected error occurred. Please retry or go back home.', retry: 'Retry', home: 'Go Home' },
  'zh-TW': { title: '發生錯誤', desc: '發生了意外錯誤，請重試或返回首頁。', retry: '重試', home: '回首頁' },
  'zh-CN': { title: '发生错误', desc: '发生了意外错误，请重试或返回首页。', retry: '重试', home: '回首页' },
};

function detectLang(): string {
  const seg = window.location.pathname.split('/')[1];
  const normalized = normalizeLangCode(seg);
  if (normalized) return normalized;
  return 'ko';
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo);
  }

  handleReload = () => {
    const lang = detectLang();
    const prefix = lang === DEFAULT_LANG ? '' : `/${lang}`;
    window.location.href = `${prefix}/`;
  };

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      const lang = detectLang();
      const t = pickByLang(i18nError, lang);

      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-6">
          <div className="max-w-md w-full text-center space-y-6">
            <div className="w-16 h-16 mx-auto rounded-full bg-destructive/10 flex items-center justify-center">
              <svg className="w-8 h-8 text-destructive" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground mb-2">{t.title}</h1>
              <p className="text-muted-foreground text-sm">{t.desc}</p>
            </div>
            {import.meta.env.DEV && this.state.error && (
              <pre className="text-xs text-left bg-muted rounded-lg p-3 overflow-auto max-h-32 text-muted-foreground">
                {this.state.error.message}
              </pre>
            )}
            <div className="flex gap-3 justify-center">
              <button
                onClick={this.handleRetry}
                className="px-5 py-2.5 rounded-xl border border-border bg-card text-foreground text-sm font-medium hover:bg-muted transition-colors"
              >
                {t.retry}
              </button>
              <button
                onClick={this.handleReload}
                className="px-5 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
              >
                {t.home}
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
